// src/RechargeManager.js
import React, { useEffect, useState } from "react";
import { db } from "./firebase";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import "./RechargeManager.css";

export default function RechargeManager() {
  const [operators, setOperators] = useState([]);
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [rechargeData, setRechargeData] = useState({});
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [newOption, setNewOption] = useState({ title: "", coins: "" });
  const [newCategory, setNewCategory] = useState("");

  useEffect(() => {
    async function fetchOperators() {
      const snap = await getDocs(collection(db, "rechargeOptions"));
      setOperators(snap.docs.map((d) => d.id));
    }
    fetchOperators();
  }, []);

  const loadRechargeOptions = async (operator) => {
    setSelectedOperator(operator);
    setSelectedCategory(null);

    const ref = doc(db, "rechargeOptions", operator);
    const snap = await getDoc(ref);

    if (!snap.exists()) {
      await setDoc(ref, { updatedAt: serverTimestamp() });
      setRechargeData({});
      return;
    }

    const data = snap.data() || {};
    setRechargeData(data);
  };

  const handleAddCategory = async () => {
    if (!selectedOperator) return;
    const cat = (newCategory || "").trim();
    if (!cat) return;

    const ref = doc(db, "rechargeOptions", selectedOperator);
    await setDoc(
      ref,
      { [cat]: [], updatedAt: serverTimestamp() },
      { merge: true }
    );

    setRechargeData((prev) => ({ ...prev, [cat]: [] }));
    setSelectedCategory(cat);
    setNewCategory("");
  };

  const handleAddOption = async () => {
    if (!selectedOperator) return;
    if (!selectedCategory) {
      alert("Please select a category first.");
      return;
    }
    const title = newOption.title.trim();
    const coinsNum = parseInt(String(newOption.coins), 10);
    if (!title || Number.isNaN(coinsNum)) return;

    const updated = [...(rechargeData[selectedCategory] || []), { title, coins: coinsNum }];

    const ref = doc(db, "rechargeOptions", selectedOperator);
    await setDoc(
      ref,
      { [selectedCategory]: updated, updatedAt: serverTimestamp() },
      { merge: true }
    );

    setRechargeData((prev) => ({ ...prev, [selectedCategory]: updated }));
    setNewOption({ title: "", coins: "" });
  };

  const handleDeleteOption = async (index) => {
    if (!selectedOperator || !selectedCategory) return;

    const updated = [...(rechargeData[selectedCategory] || [])];
    updated.splice(index, 1);

    const ref = doc(db, "rechargeOptions", selectedOperator);
    await setDoc(
      ref,
      { [selectedCategory]: updated, updatedAt: serverTimestamp() },
      { merge: true }
    );

    setRechargeData((prev) => ({ ...prev, [selectedCategory]: updated }));
  };

  return (
    <div className="recharge-manager">
      <h2>Recharge Manager</h2>

      <div className="operator-list">
        {operators.map((op) => (
          <button
            key={op}
            onClick={() => loadRechargeOptions(op)}
            className={selectedOperator === op ? "active" : ""}
          >
            {op}
          </button>
        ))}
      </div>

      {selectedOperator && (
        <>
          <div className="category-section">
            <h3>{selectedOperator} Categories</h3>

            <div className="add-category">
              <input
                type="text"
                placeholder="New category (e.g., Prepaid, Data, DTH)"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
              />
              <button onClick={handleAddCategory}>➕ Add Category</button>
            </div>

            <div className="category-list">
              {Object.keys(rechargeData)
                .filter((k) => k !== "updatedAt")
                .map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={selectedCategory === cat ? "active" : ""}
                  >
                    {cat}
                  </button>
                ))}
            </div>
          </div>

          {selectedCategory && (
            <div className="option-section">
              <h4>{selectedCategory} Options</h4>
              <ul className="option-list">
                {(rechargeData[selectedCategory] || []).map((item, idx) => (
                  <li key={idx} className="option-item">
                    <strong>{item.title}</strong> — {item.coins} coins
                    <button onClick={() => handleDeleteOption(idx)} className="delete-btn">
                      ❌ Delete
                    </button>
                  </li>
                ))}
              </ul>

              <div className="add-option">
                <input
                  type="text"
                  placeholder="Title (e.g., 1.5GB/day 84 days)"
                  value={newOption.title}
                  onChange={(e) => setNewOption({ ...newOption, title: e.target.value })}
                />
                <input
                  type="number"
                  placeholder="Coins"
                  value={newOption.coins}
                  onChange={(e) => setNewOption({ ...newOption, coins: e.target.value })}
                />
                <button onClick={handleAddOption}>➕ Add Option</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
